import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { api } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_ADMIN_EMAIL = 'safeen@thes.dev';

interface UserRow {
  id: number;
  email: string;
  name: string | null;
  role: 'ADMIN' | 'USER';
  createdAt: string;
}

function isDefaultAdmin(u: UserRow) {
  return u.email.toLowerCase() === DEFAULT_ADMIN_EMAIL.toLowerCase();
}

export default function Users() {
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'ADMIN' as 'ADMIN' | 'USER',
  });
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);
  const [addEditDialogOpen, setAddEditDialogOpen] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/users');
      if (!data) {
        toast.error(t('users.loadError'));
      }
      setUsers(data?.users ?? []);
    } catch {
      toast.error(t('users.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const resetForm = () => {
    setForm({ email: '', password: '', name: '', role: 'ADMIN' });
    setEditing(null);
  };

  const openAddDialog = () => {
    resetForm();
    setAddEditDialogOpen(true);
  };

  const openEditDialog = (u: UserRow) => {
    if (isDefaultAdmin(u)) return;
    setEditing(u);
    setForm({
      email: u.email,
      password: '',
      name: u.name ?? '',
      role: u.role,
    });
    setAddEditDialogOpen(true);
  };

  const closeAddEditDialog = () => {
    setAddEditDialogOpen(false);
    resetForm();
  };

  const handleCreate = async () => {
    if (!form.email || !form.password) {
      toast.error(t('users.requiredError'));
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/users', {
        email: form.email,
        password: form.password,
        name: form.name || null,
        role: form.role,
      });
      toast.success(t('users.createSuccess'));
      closeAddEditDialog();
      fetchUsers();
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? t('users.createError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!editing) return;
    setSubmitting(true);
    try {
      await api.put(`/api/users/${editing.id}`, {
        email: form.email,
        password: form.password || undefined,
        name: form.name || null,
        role: form.role,
      });
      toast.success(t('users.updateSuccess'));
      closeAddEditDialog();
      fetchUsers();
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? t('users.updateError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || isDefaultAdmin(deleteTarget)) return;
    try {
      await api.delete(`/api/users/${deleteTarget.id}`);
      toast.success(t('users.deleteSuccess'));
      setDeleteTarget(null);
      fetchUsers();
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? t('users.deleteError'));
    }
  };

  if (currentUser?.role !== 'ADMIN') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">{t('users.accessDenied')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/settings">
              <Button
                variant="outline"
                size="sm"
                className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('users.back')}
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
                {t('users.title')}
              </h1>
              <p className="text-slate-500 text-sm mt-0.5">{t('users.subtitle')}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <div>
              <CardTitle>{t('users.title')}</CardTitle>
              <CardDescription>{t('users.cardDescription')}</CardDescription>
            </div>
            <Button
              onClick={openAddDialog}
              className="bg-slate-900 hover:bg-slate-800"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('users.addUser')}
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
              </div>
            ) : users.length === 0 ? (
              <div className="rounded-2xl bg-white border border-slate-200 p-12 text-center text-slate-500">
                {t('users.noUsers')}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('users.email')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('users.name')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('users.role')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('users.created')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm text-right">
                      {t('users.actions')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} className="hover:bg-slate-50">
                      <TableCell className="text-sm text-slate-700">{u.email}</TableCell>
                      <TableCell className="text-sm text-slate-700">{u.name ?? '—'}</TableCell>
                      <TableCell>
                        <Badge variant={u.role === 'ADMIN' ? 'default' : 'secondary'}>
                          {u.role === 'ADMIN' ? t('users.admin') : t('users.user')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 tabular-nums">
                        {new Date(u.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800"
                            disabled={isDefaultAdmin(u)}
                            onClick={() => openEditDialog(u)}
                            title={isDefaultAdmin(u) ? t('users.defaultAdminNoEdit') : t('users.edit')}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            disabled={u.id === currentUser?.id || isDefaultAdmin(u)}
                            onClick={() => setDeleteTarget(u)}
                            title={
                              isDefaultAdmin(u)
                                ? t('users.defaultAdminNoDelete')
                                : u.id === currentUser?.id
                                  ? t('users.cannotDeleteSelf')
                                  : t('users.delete')
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      <Dialog open={addEditDialogOpen} onOpenChange={(open) => !open && closeAddEditDialog()}>
        <DialogContent className="rounded-2xl border border-slate-200">
          <DialogHeader>
            <DialogTitle>{editing ? t('users.editUser') : t('users.addUser')}</DialogTitle>
            <DialogDescription>
              {editing ? t('users.editUserDescription') : t('users.addUserDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>{t('users.email')}</Label>
              <Input
                type="email"
                placeholder={t('users.emailPlaceholder')}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('login.password')} {editing && t('users.passwordLeaveBlank')}</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('users.nameOptional')}</Label>
              <Input
                placeholder={t('users.displayNamePlaceholder')}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-lg"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('users.role')}</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v as 'ADMIN' | 'USER' })}
              >
                <SelectTrigger className="rounded-lg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMIN">{t('users.admin')}</SelectItem>
                  <SelectItem value="USER">{t('users.user')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeAddEditDialog} className="border-slate-200">
              {t('users.cancel')}
            </Button>
            {editing ? (
              <Button
                onClick={handleUpdate}
                disabled={!form.email || submitting}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {t('users.update')}
              </Button>
            ) : (
              <Button
                onClick={handleCreate}
                disabled={!form.email || !form.password || submitting}
                className="bg-slate-900 hover:bg-slate-800"
              >
                {t('users.addUser')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="rounded-2xl border border-slate-200">
          <DialogHeader>
            <DialogTitle>{t('users.deleteUser')}</DialogTitle>
            <DialogDescription>
              {t('users.willPermanentlyRemove')} {deleteTarget?.email}. {t('users.confirmDeleteUser')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              {t('users.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {t('users.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
